import React, { useEffect, useState } from 'react';
import { getRutas, createRuta, asignarCobrador, getUsers } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Map, Users, DollarSign, AlertTriangle, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 0,
    }).format(value);
};

export default function RutasPage() {
    const { hasRole } = useAuth();
    const [rutas, setRutas] = useState([]);
    const [cobradores, setCobradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [selectedRuta, setSelectedRuta] = useState(null);
    const [form, setForm] = useState({
        nombre: '',
        zona: '',
        descripcion: '',
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [rutasRes, usersRes] = await Promise.all([
                getRutas(),
                getUsers()
            ]);
            setRutas(rutasRes.data);
            setCobradores(usersRes.data.filter(u => u.rol === 'cobrador'));
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await createRuta(form);
            toast.success('Ruta creada exitosamente');
            setDialogOpen(false);
            setForm({ nombre: '', zona: '', descripcion: '' });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear ruta');
        }
    };

    const handleAssign = async (cobradorId) => {
        if (!selectedRuta) return;
        try {
            await asignarCobrador(selectedRuta.id, cobradorId);
            toast.success('Cobrador asignado exitosamente');
            setAssignDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error('Error al asignar cobrador');
        }
    };

    const openAssignDialog = (ruta) => {
        setSelectedRuta(ruta);
        setAssignDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="rutas-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Rutas de Cobro</h1>
                    <p className="text-muted-foreground mt-1">{rutas.length} rutas configuradas</p>
                </div>
                {hasRole(['admin']) && (
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-ruta-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Nueva Ruta
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-heading">Nueva Ruta</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="nombre">Nombre de la Ruta</Label>
                                    <Input
                                        id="nombre"
                                        value={form.nombre}
                                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                                        required
                                        placeholder="Ej: Ruta Norte"
                                        data-testid="ruta-nombre-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="zona">Zona/Sector</Label>
                                    <Input
                                        id="zona"
                                        value={form.zona}
                                        onChange={(e) => setForm({ ...form, zona: e.target.value })}
                                        required
                                        placeholder="Ej: Zona Norte"
                                        data-testid="ruta-zona-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="descripcion">Descripción (opcional)</Label>
                                    <Input
                                        id="descripcion"
                                        value={form.descripcion}
                                        onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                                        placeholder="Descripción de la ruta..."
                                    />
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" data-testid="ruta-submit-btn">
                                        Crear Ruta
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Rutas Grid */}
            {rutas.length === 0 ? (
                <Card className="p-12 text-center">
                    <Map className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No hay rutas configuradas</p>
                    <p className="text-sm text-muted-foreground mt-1">
                        Crea tu primera ruta para organizar los cobros
                    </p>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rutas.map((ruta) => (
                        <Card key={ruta.id} className="card-hover animate-fadeIn">
                            <CardHeader className="pb-2">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <CardTitle className="font-heading text-lg">{ruta.nombre}</CardTitle>
                                        <p className="text-sm text-muted-foreground">{ruta.zona}</p>
                                    </div>
                                    <div className="p-2 rounded-lg bg-primary/10">
                                        <Map className="w-5 h-5 text-primary" />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {/* Cobrador */}
                                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-muted-foreground" />
                                        <span className="text-sm">
                                            {ruta.cobrador_nombre || 'Sin asignar'}
                                        </span>
                                    </div>
                                    {hasRole(['admin']) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => openAssignDialog(ruta)}
                                            data-testid={`assign-cobrador-${ruta.id}`}
                                        >
                                            <UserPlus className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="p-3 bg-accent/5 rounded-lg">
                                        <div className="flex items-center gap-2 text-accent mb-1">
                                            <DollarSign className="w-4 h-4" />
                                            <span className="text-xs font-medium">Prestado</span>
                                        </div>
                                        <p className="font-mono font-bold">{formatCurrency(ruta.total_prestado)}</p>
                                    </div>
                                    <div className="p-3 bg-primary/5 rounded-lg">
                                        <div className="flex items-center gap-2 text-primary mb-1">
                                            <DollarSign className="w-4 h-4" />
                                            <span className="text-xs font-medium">Cobrado</span>
                                        </div>
                                        <p className="font-mono font-bold">{formatCurrency(ruta.total_cobrado)}</p>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-2 border-t">
                                    <div className="flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-destructive" />
                                        <span className="text-sm">Mora</span>
                                    </div>
                                    <span className="font-mono font-bold text-destructive">
                                        {formatCurrency(ruta.total_mora)}
                                    </span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-muted-foreground">Clientes</span>
                                    <Badge variant="secondary">{ruta.clientes_count}</Badge>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Assign Dialog */}
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Asignar Cobrador</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Selecciona un cobrador para la ruta: <strong>{selectedRuta?.nombre}</strong>
                        </p>
                        {cobradores.length === 0 ? (
                            <p className="text-center py-4 text-muted-foreground">
                                No hay cobradores disponibles
                            </p>
                        ) : (
                            <div className="space-y-2">
                                {cobradores.map((cobrador) => (
                                    <Button
                                        key={cobrador.id}
                                        variant="outline"
                                        className="w-full justify-start"
                                        onClick={() => handleAssign(cobrador.id)}
                                        data-testid={`select-cobrador-${cobrador.id}`}
                                    >
                                        <Users className="w-4 h-4 mr-2" />
                                        {cobrador.nombre}
                                        {cobrador.ruta_id && (
                                            <Badge variant="secondary" className="ml-auto">
                                                Ya asignado
                                            </Badge>
                                        )}
                                    </Button>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
